// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBaseEffects
// ─────────────────────────────────────────────────────────────────────────────
// Side-effects for the Knowledge Base document store slice.
// Responsibilities:
//  - Load documents from API
//  - Upload, update, and delete documents
//  - Download documents
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { KnowledgeBaseService } from '../services/knowledge-base.service';
import * as KbActions from './knowledge-base.actions';

@Injectable()
export class KnowledgeBaseEffects {
  constructor(
    private actions$: Actions,
    private knowledgeBaseService: KnowledgeBaseService,
  ) {}

  // ── Load documents ───────────────────────────────────────────────────────

  loadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KbActions.LoadDocuments),
      switchMap(() =>
        this.knowledgeBaseService.getDocuments().pipe(
          map((documents) => KbActions.LoadDocumentsSuccess({ documents })),
          catchError((error) => of(KbActions.LoadDocumentsError({ error: error.message }))),
        ),
      ),
    ),
  );

  // ── Upload document ──────────────────────────────────────────────────────

  uploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KbActions.UploadDocument),
      mergeMap(({ request }) =>
        this.knowledgeBaseService.uploadDocument(request).pipe(
          map((document) => KbActions.UploadDocumentSuccess({ document })),
          catchError((error) => of(KbActions.UploadDocumentError({ error: error.error?.error || error.message }))),
        ),
      ),
    ),
  );

  // ── Delete document ──────────────────────────────────────────────────────

  deleteDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KbActions.DeleteDocuments),
      mergeMap(({ documentIds }) =>
        this.knowledgeBaseService.deleteDocuments(documentIds).pipe(
          map(() => KbActions.DeleteDocumentsSuccess({ documentIds })),
          catchError((error) => of(KbActions.DeleteDocumentsError({ error: error.message }))),
        ),
      ),
    ),
  );

  // ── Update document (file + metadata via dialog) ────────────────────────

  updateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KbActions.UpdateDocument),
      mergeMap(({ request }) =>
        this.knowledgeBaseService.updateDocument(request).pipe(
          map((document) => KbActions.UpdateDocumentSuccess({ document })),
          catchError((error) => of(KbActions.UpdateDocumentError({ error: error.error?.error || error.message }))),
        ),
      ),
    ),
  );

  // ── Download document ────────────────────────────────────────────────────

  downloadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KbActions.DownloadDocuments),
      mergeMap(({ documentIds }) =>
        this.knowledgeBaseService.downloadDocuments(documentIds).pipe(
          tap((blob) => {
            const filename = documentIds.length === 1 ? 'document' : `KB-Documents-${new Date().toISOString().slice(0, 10)}.zip`;
            this.knowledgeBaseService.triggerBlobDownload(blob, filename);
          }),
          map(() => KbActions.DownloadDocumentsSuccess({ documentIds })),
          catchError((error) => of(KbActions.DownloadDocumentsError({ error: error.message }))),
        ),
      ),
    ),
  );
}
